import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import SlideDialogV from '../../../components/common/SlideDialogV';
import Typography from '../../../components/common/Typography';
import { getCurrentYear } from '../../../common/Util';

// AI 결과에서 표시할 필수 항목 매핑
const DISPLAY_FIELDS = [
  { key: 'total', labelKey: 'total' },
  { key: 'htm005', labelKey: 'htm005' },
  { key: 'htm019', endKey: 'htm020', labelKey: 'workPeriod', isDateRange: true },
  { key: 'htm014', labelKey: 'htm014' },
  { key: 'htm012', labelKey: 'htm012' },
  { key: 'htm013', labelKey: 'htm013' },
  { key: 'htm015', labelKey: 'htm015' },
  { key: 'htm016', labelKey: 'htm016' },
];

const ContentContainer = styled.div`
  padding-top: 16px;
  padding-bottom: 16px;
  border-top: 1px solid #dddddd;
  border-bottom: 1px solid #dddddd;
  margin-bottom: 30px;
`;

const DataList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DataRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EmptyContainer = styled.div`
  border: 1px dashed #dddddd;
  border-radius: 16px;
  padding: 32px 16px;
  text-align: center;
`;

const WarningMessage = styled.div`
  color: #ff0000;
  margin-top: 4px;
`;

/**
 * 날짜 형식 변환 (YYYY-MM-DD -> YYYY.MM.DD)
 */
const formatDateDisplay = dateStr => {
  if (!dateStr) return '';
  return dateStr.replace(/-/g, '.');
};

/**
 * 금액 포맷팅 (콤마 추가)
 */
const formatAmount = value => {
  if (!value) return '';
  // 이미 콤마가 있으면 그대로 반환
  if (value.includes(',')) return value;
  // 숫자만 추출 후 콤마 추가
  const numericValue = value.replace(/[^0-9]/g, '');
  if (!numericValue) return value;
  return Number(numericValue).toLocaleString();
};

/**
 * 근무기간이 당해년도인지 확인
 */
const isCurrentYearWorkPeriod = (startDate, endDate) => {
  const currentYear = getCurrentYear();
  const startYear = startDate?.substring(0, 4);
  const endYear = endDate?.substring(0, 4);
  return startYear == currentYear || endYear == currentYear;
};

/**
 * AI 분석 결과 확인 다이얼로그
 */
const AIResultDialog = ({ isOpen, onClose, aiData }) => {
  const { t } = useTranslation('previousEmployment');

  // 표시할 데이터 필터링 (값이 있는 항목만)
  const displayItems = DISPLAY_FIELDS.filter(field => {
    if (field.isDateRange) {
      return aiData?.[field.key] && aiData?.[field.endKey];
    }
    return aiData?.[field.key];
  }).slice(0, 8); // 최대 8개

  const hasData = displayItems.length > 0;

  // 근무기간이 당해년도인지 확인
  const workPeriodField = DISPLAY_FIELDS.find(f => f.isDateRange);
  const isNotCurrentYear =
    workPeriodField &&
    aiData?.[workPeriodField.key] &&
    aiData?.[workPeriodField.endKey] &&
    !isCurrentYearWorkPeriod(aiData[workPeriodField.key], aiData[workPeriodField.endKey]);

  const renderContent = () => {
    if (!hasData) {
      return (
        <EmptyContainer>
          <Typography variant="body1Medium" style={{ color: '#999999', whiteSpace: 'pre-wrap' }}>
            {t('aiResultDialog.emptyMessage')}
          </Typography>
        </EmptyContainer>
      );
    }

    return (
      <ContentContainer>
        <DataList>
          {displayItems.map(field => {
            let value = '';
            if (field.isDateRange) {
              const startDate = formatDateDisplay(aiData[field.key]);
              const endDate = formatDateDisplay(aiData[field.endKey]);
              value = `${startDate} ~ ${endDate}`;
            } else {
              value = formatAmount(aiData[field.key]);
            }

            const isWorkPeriodNotCurrentYear = field.isDateRange && isNotCurrentYear;

            return (
              <DataRow key={field.key}>
                <Typography variant={field.key == 'total' ? 'body2Bold' : 'body2Medium'}>
                  {t(`aiResultDialog.fields.${field.labelKey}`)}
                </Typography>
                <Typography
                  variant={field.key == 'total' ? 'body2Bold' : 'body2Medium'}
                  style={isWorkPeriodNotCurrentYear ? { color: '#ff0000' } : {}}>
                  {value} {!field.isDateRange && '원'}
                </Typography>
              </DataRow>
            );
          })}
        </DataList>
      </ContentContainer>
    );
  };

  const renderDescription = () => (
    <>
      {t('aiResultDialog.description')}
      {isNotCurrentYear && (
        <WarningMessage>
          <Typography variant="captionMedium" style={{ color: '#ff0000' }}>
            * 당해년도 근무기간이 아닙니다
          </Typography>
        </WarningMessage>
      )}
    </>
  );

  return (
    <SlideDialogV
      isOpen={isOpen}
      onClose={onClose}
      title={t('aiResultDialog.title')}
      description={renderDescription()}
      innertComponent={renderContent()}
      buttons={[
        {
          label: t('aiResultDialog.confirmButton'),
          onClick: onClose,
          color: 'primary',
        },
      ]}
    />
  );
};

export default AIResultDialog;
